import React, { useEffect, useState } from 'react';
import { Check, Plus, X, Save, Clapperboard, User, Video } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';
import { FASI } from '../lib/produzione';
import PersonaPicker from './PersonaPicker';

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * Assegnatari, fase di produzione e scheda di produzione (brief, shot list, location…)
 * di un contenuto. Salva con PATCH /ped/{rid}/produzione (update parziale).
 * Props: riga (dal GET /ped/riga), onSaved(rigaAggiornata)
 */
export default function ProduzionePanel({ riga, onSaved }) {
  const { success } = useToast();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nuovoShot, setNuovoShot] = useState('');

  // Il form si reinizializza solo cambiando contenuto: le modifiche non salvate alla
  // scheda sopravvivono ai ricaricamenti della riga (es. dopo un cambio fase).
  useEffect(() => {
    if (!riga) return;
    setF({
      responsabile_id: riga.responsabile_id || null,
      videomaker_id: riga.videomaker_id || null,
      data_riprese: toLocalInput(riga.data_riprese),
      scadenza_montaggio: riga.scadenza_montaggio || '',
      produzione: { shot_list: [], ...(riga.produzione || {}) },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riga?.id]);

  if (!riga || !f) return null;

  const patch = async (body, msg) => {
    setSaving(true);
    try {
      const r = await api.patch(`/ped/${riga.id}/produzione`, body);
      if (msg) success(msg);
      onSaved && onSaved(r.data);
    } catch { /* toast globale */ }
    setSaving(false);
  };

  const setP = (k, v) => setF(x => ({ ...x, produzione: { ...x.produzione, [k]: v } }));
  const shots = Array.isArray(f.produzione.shot_list) ? f.produzione.shot_list : [];

  const salvaScheda = () => patch({
    responsabile_id: f.responsabile_id, videomaker_id: f.videomaker_id,
    data_riprese: f.data_riprese ? new Date(f.data_riprese).toISOString() : null,
    scadenza_montaggio: f.scadenza_montaggio || null,
    produzione: f.produzione,
  }, 'Produzione salvata');

  const assegna = (k, v) => { setF(x => ({ ...x, [k]: v })); patch({ [k]: v }, v ? 'Assegnato' : 'Assegnazione rimossa'); };

  const cambiaFase = (key) => {
    if (key === riga.fase_produzione) return;
    if (key === 'approvato' && !can(user, RUOLI_SOCIAL)) return;
    patch({ fase_produzione: key }, 'Fase aggiornata');
  };

  const faseIdx = FASI.findIndex(x => x.key === riga.fase_produzione);
  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
  const label = (t) => <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 5, fontWeight: 500 }}>{t}</label>;

  return (
    <div>
      {/* Persone */}
      <div className="eden-grid2" style={grid2}>
        <div>
          {label(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><User size={12} /> Responsabile (social media manager)</span>)}
          <PersonaPicker value={f.responsabile_id} onChange={v => assegna('responsabile_id', v)} preferRuoli={['social_manager']} disabled={saving} />
        </div>
        <div>
          {label(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Video size={12} /> Videomaker</span>)}
          <PersonaPicker value={f.videomaker_id} onChange={v => assegna('videomaker_id', v)} preferRuoli={['videomaker']} disabled={saving} />
        </div>
      </div>

      {/* Fase */}
      <div style={{ marginTop: 16 }}>
        {label(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clapperboard size={12} /> Fase di produzione video</span>)}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FASI.map((x, i) => {
            const on = x.key === riga.fase_produzione;
            const fatto = faseIdx >= 0 && i < faseIdx;
            const bloccato = x.key === 'approvato' && !can(user, RUOLI_SOCIAL);
            return (
              <button key={x.key} onClick={() => cambiaFase(x.key)} disabled={saving || bloccato}
                title={bloccato ? 'Approva il social media manager' : ''}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: on ? 700 : 500, padding: '6px 10px', borderRadius: 8,
                  cursor: saving || bloccato ? 'default' : 'pointer', opacity: bloccato ? 0.5 : 1,
                  border: `1px solid ${on ? x.color : 'var(--border)'}`, background: on ? 'var(--bg3)' : 'var(--surface)',
                  color: on ? x.color : fatto ? 'var(--text-muted)' : 'var(--text-dim)' }}>
                {fatto ? <Check size={11} /> : <span style={{ width: 6, height: 6, borderRadius: '50%', background: x.color }} />}
                {x.label}
              </button>
            );
          })}
          {riga.fase_produzione && (
            <button onClick={() => patch({ fase_produzione: null }, 'Pipeline video disattivata')} disabled={saving}
              title="Togli questo contenuto dalla pipeline video"
              style={{ fontSize: 11.5, padding: '6px 9px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text-dim)', cursor: 'pointer' }}>
              Nessuna
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
          La fase aggiorna lo stato del PED. Caricare una versione porta in «Review interna»; il cliente vede il video solo da «Review cliente».
        </div>
      </div>

      {/* Date */}
      <div className="eden-grid2" style={{ ...grid2, marginTop: 16 }}>
        <div>
          {label('Riprese (data e ora)')}
          <input type="datetime-local" className="eden-input" value={f.data_riprese} onChange={e => setF(x => ({ ...x, data_riprese: e.target.value }))} />
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>Crea l'evento «Riprese» nel calendario del team</div>
        </div>
        <div>
          {label('Consegna montaggio')}
          <input type="date" className="eden-input" value={f.scadenza_montaggio} onChange={e => setF(x => ({ ...x, scadenza_montaggio: e.target.value }))} />
        </div>
      </div>

      {/* Scheda */}
      <div style={{ marginTop: 16 }}>
        {label('Brief / concept')}
        <textarea className="eden-input" value={f.produzione.brief || ''} onChange={e => setP('brief', e.target.value)}
          placeholder="Obiettivo, messaggio, riferimenti, mood…" style={{ minHeight: 80, resize: 'vertical' }} />
      </div>
      <div style={{ marginTop: 12 }}>
        {label('Script / scaletta')}
        <textarea className="eden-input" value={f.produzione.script || ''} onChange={e => setP('script', e.target.value)}
          placeholder="Battute, voice over, testi a schermo…" style={{ minHeight: 80, resize: 'vertical' }} />
      </div>

      <div style={{ marginTop: 12 }}>
        {label(`Shot list (${shots.filter(s => s.fatto).length}/${shots.length})`)}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {shots.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 7, background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <input type="checkbox" checked={!!s.fatto} style={{ accentColor: 'var(--green)' }}
                onChange={() => setP('shot_list', shots.map((x, j) => j === i ? { ...x, fatto: !x.fatto } : x))} />
              <input value={s.testo} onChange={e => setP('shot_list', shots.map((x, j) => j === i ? { ...x, testo: e.target.value } : x))}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: s.fatto ? 'var(--text-dim)' : 'var(--text)', fontSize: 12.5, textDecoration: s.fatto ? 'line-through' : 'none' }} />
              <button onClick={() => setP('shot_list', shots.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', display: 'flex', padding: 0 }}><X size={13} /></button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="eden-input" value={nuovoShot} onChange={e => setNuovoShot(e.target.value)} placeholder="es. Dettaglio prodotto su banco, luce laterale"
              onKeyDown={e => { if (e.key === 'Enter' && nuovoShot.trim()) { setP('shot_list', [...shots, { testo: nuovoShot.trim(), fatto: false }]); setNuovoShot(''); } }} />
            <button onClick={() => { if (nuovoShot.trim()) { setP('shot_list', [...shots, { testo: nuovoShot.trim(), fatto: false }]); setNuovoShot(''); } }}
              style={{ flexShrink: 0, width: 38, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Plus size={15} />
            </button>
          </div>
        </div>
      </div>

      <div className="eden-grid2" style={{ ...grid2, marginTop: 12 }}>
        <div>{label('Location')}<input className="eden-input" value={f.produzione.location || ''} onChange={e => setP('location', e.target.value)} placeholder="Indirizzo / studio" /></div>
        <div>{label('Talent / persone in video')}<input className="eden-input" value={f.produzione.talent || ''} onChange={e => setP('talent', e.target.value)} placeholder="Chi appare" /></div>
        <div>{label('Attrezzatura')}<input className="eden-input" value={f.produzione.attrezzatura || ''} onChange={e => setP('attrezzatura', e.target.value)} placeholder="Camera, gimbal, luci, microfoni…" /></div>
        <div>{label('Musica / audio')}<input className="eden-input" value={f.produzione.musica || ''} onChange={e => setP('musica', e.target.value)} placeholder="Brano, licenza, trending audio…" /></div>
      </div>
      <div style={{ marginTop: 12 }}>
        {label('Note per il videomaker')}
        <textarea className="eden-input" value={f.produzione.note || ''} onChange={e => setP('note', e.target.value)} style={{ minHeight: 60, resize: 'vertical' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button onClick={salvaScheda} disabled={saving} className="eden-btn-primary" style={{ fontSize: 12.5 }}>
          <Save size={14} /> {saving ? 'Salvo…' : 'Salva produzione'}
        </button>
      </div>
    </div>
  );
}
